/*
 * Copyright © 2025 AionTrack. All rights reserved.
 */

/**
 * React Query hooks for VFX shots
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import type {
  CreateVfxShotInput,
  UpdateVfxShotInput,
  VfxShotAssignRequest,
  VfxShotStatusUpdateRequest,
} from '@/types/database'
import * as vfxShotsApi from '@/lib/api/vfxShots'
import { toast } from 'sonner'

// =============================================
// QUERY KEYS
// =============================================

export const vfxShotKeys = {
  all: ['vfx-shots'] as const,
  lists: () => [...vfxShotKeys.all, 'list'] as const,
  list: (projectId: string, filters?: Record<string, any>) =>
    [...vfxShotKeys.lists(), projectId, filters] as const,
  details: () => [...vfxShotKeys.all, 'detail'] as const,
  detail: (id: string) => [...vfxShotKeys.details(), id] as const,
  notifications: (userId: string) =>
    [...vfxShotKeys.all, 'notifications', userId] as const,
}

// =============================================
// VFX SHOT QUERIES
// =============================================

/**
 * Fetch VFX shots for a project
 */
export function useVfxShots(
  projectId: string,
  options?: {
    status?: string
    assigned_user_id?: string
    limit?: number
    offset?: number
  }
) {
  return useQuery({
    queryKey: vfxShotKeys.list(projectId, options),
    queryFn: async () => {
      // Add timeout to prevent infinite hanging
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 15000) // 15 second timeout

      try {
        const result = await vfxShotsApi.listVfxShots(projectId, options)
        clearTimeout(timeoutId)
        return result
      } catch (error) {
        clearTimeout(timeoutId)
        throw error
      }
    },
    enabled: !!projectId,
    retry: 1, // Only retry once
    retryDelay: 1000,
    staleTime: 0, // Always consider data stale - refetch on every invalidation
    refetchOnMount: 'always', // Always refetch when component mounts, even if data exists
    refetchOnWindowFocus: false, // Don't refetch on window focus (can be annoying)
  })
}

/**
 * Fetch a single VFX shot
 */
export function useVfxShot(shotId: string) {
  return useQuery({
    queryKey: vfxShotKeys.detail(shotId),
    queryFn: () => vfxShotsApi.getVfxShot(shotId),
    enabled: !!shotId,
    staleTime: 0, // Always consider data stale
    refetchOnMount: 'always', // Always refetch when detail view opens
    refetchOnWindowFocus: false, // Don't refetch on window focus
  })
}

/**
 * Fetch user's VFX notifications
 */
export function useVfxNotifications(
  userId: string,
  options?: {
    unread_only?: boolean
    limit?: number
  }
) {
  return useQuery({
    queryKey: vfxShotKeys.notifications(userId),
    queryFn: () => vfxShotsApi.getUserVfxNotifications(userId, options),
    enabled: !!userId,
  })
}

// =============================================
// VFX SHOT MUTATIONS
// =============================================

/**
 * Create a new VFX shot
 */
export function useCreateVfxShot() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      projectId,
      shotData,
    }: {
      projectId: string
      shotData: CreateVfxShotInput
    }) => vfxShotsApi.createVfxShot(projectId, shotData),
    onSuccess: (_, variables) => {
      // Invalidate all list queries for this project regardless of filters
      queryClient.invalidateQueries({
        predicate: (query) => {
          const queryKey = query.queryKey
          return (
            Array.isArray(queryKey) &&
            queryKey[0] === 'vfx-shots' &&
            queryKey[1] === 'list' &&
            queryKey[2] === variables.projectId
          )
        },
      })
      toast.success('VFX shot created successfully')
    },
    onError: (error: Error) => {
      toast.error(`Failed to create VFX shot: ${error.message}`)
    },
  })
}

/**
 * Update a VFX shot
 */
export function useUpdateVfxShot() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      shotId,
      updates,
    }: {
      shotId: string
      updates: UpdateVfxShotInput
    }) => vfxShotsApi.updateVfxShot(shotId, updates),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: vfxShotKeys.detail(data.id) })
      // Invalidate all list queries for this project regardless of filters
      queryClient.invalidateQueries({
        predicate: (query) => {
          const queryKey = query.queryKey
          return (
            Array.isArray(queryKey) &&
            queryKey[0] === 'vfx-shots' &&
            queryKey[1] === 'list' &&
            queryKey[2] === data.project_id
          )
        },
      })
      toast.success('VFX shot updated successfully')
    },
    onError: (error: Error) => {
      toast.error(`Failed to update VFX shot: ${error.message}`)
    },
  })
}

/**
 * Delete a VFX shot
 */
export function useDeleteVfxShot() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ shotId }: { shotId: string; projectId: string }) =>
      vfxShotsApi.deleteVfxShot(shotId),
    onMutate: async (variables) => {
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({
        predicate: (query) => {
          const queryKey = query.queryKey
          return (
            Array.isArray(queryKey) &&
            queryKey[0] === 'vfx-shots' &&
            queryKey[1] === 'list' &&
            queryKey[2] === variables.projectId
          )
        },
      })

      // Snapshot the previous value
      const previousShots = queryClient.getQueriesData({
        predicate: (query) => {
          const queryKey = query.queryKey
          return (
            Array.isArray(queryKey) &&
            queryKey[0] === 'vfx-shots' &&
            queryKey[1] === 'list' &&
            queryKey[2] === variables.projectId
          )
        },
      })

      // Optimistically remove the shot from all matching queries
      queryClient.setQueriesData(
        {
          predicate: (query) => {
            const queryKey = query.queryKey
            return (
              Array.isArray(queryKey) &&
              queryKey[0] === 'vfx-shots' &&
              queryKey[1] === 'list' &&
              queryKey[2] === variables.projectId
            )
          },
        },
        (old: any) => {
          if (!old) return old
          return {
            ...old,
            shots: old.shots.filter((shot: any) => shot.id !== variables.shotId),
            total: old.total - 1,
          }
        }
      )

      // Return context with the previous data
      return { previousShots }
    },
    onSuccess: (_, variables) => {
      // Invalidate to refetch and ensure consistency
      queryClient.invalidateQueries({
        predicate: (query) => {
          const queryKey = query.queryKey
          return (
            Array.isArray(queryKey) &&
            queryKey[0] === 'vfx-shots' &&
            queryKey[1] === 'list' &&
            queryKey[2] === variables.projectId
          )
        },
      })
      toast.success('VFX shot deleted successfully')
    },
    onError: (error: Error, _variables, context: any) => {
      // Rollback on error
      if (context?.previousShots) {
        context.previousShots.forEach(([queryKey, data]: [any, any]) => {
          queryClient.setQueryData(queryKey, data)
        })
      }
      toast.error(`Failed to delete VFX shot: ${error.message}`)
    },
  })
}

/**
 * Assign VFX shots to an artist
 */
export function useAssignVfxShots() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      projectId,
      assignRequest,
    }: {
      projectId: string
      assignRequest: VfxShotAssignRequest
    }) => vfxShotsApi.assignVfxShots(projectId, assignRequest),
    onSuccess: async (data, variables) => {
      // Invalidate all list queries for this project regardless of filters
      await queryClient.invalidateQueries({
        predicate: (query) => {
          const queryKey = query.queryKey
          return (
            Array.isArray(queryKey) &&
            queryKey[0] === 'vfx-shots' &&
            queryKey[1] === 'list' &&
            queryKey[2] === variables.projectId
          )
        },
      })
      // Invalidate individual shot queries
      await Promise.all(
        data.map((shot) =>
          queryClient.invalidateQueries({ queryKey: vfxShotKeys.detail(shot.id) })
        )
      )
      // Invalidate dashboard queries (so AssignedToMe section updates)
      // Use prefix matching to invalidate all user-specific assignment queries
      await queryClient.invalidateQueries({
        predicate: (query) => {
          const queryKey = query.queryKey
          return (
            Array.isArray(queryKey) &&
            queryKey[0] === 'dashboard' &&
            (queryKey[1] === 'myAssignedClips' ||
             queryKey[1] === 'myAssignedVfxShots' ||
             (queryKey[1] === 'projectAssignedClips' && queryKey[2] === variables.projectId) ||
             (queryKey[1] === 'projectAssignedVfxShots' && queryKey[2] === variables.projectId))
          )
        },
      })
      toast.success(`${data.length} shot(s) assigned successfully`)
    },
    onError: (error: Error) => {
      toast.error(`Failed to assign shots: ${error.message}`)
    },
  })
}

/**
 * Update VFX shot status
 */
export function useUpdateVfxShotStatus() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      shotId,
      statusUpdate,
    }: {
      shotId: string
      statusUpdate: VfxShotStatusUpdateRequest
    }) => vfxShotsApi.updateVfxShotStatus(shotId, statusUpdate),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: vfxShotKeys.detail(data.id) })
      // Invalidate all list queries for this project regardless of filters
      queryClient.invalidateQueries({
        predicate: (query) => {
          const queryKey = query.queryKey
          return (
            Array.isArray(queryKey) &&
            queryKey[0] === 'vfx-shots' &&
            queryKey[1] === 'list' &&
            queryKey[2] === data.project_id
          )
        },
      })
      toast.success(`Status updated to "${data.status}"`)
    },
    onError: (error: Error) => {
      toast.error(`Failed to update status: ${error.message}`)
    },
  })
}

/**
 * Detect VFX shots from a commit
 */
export function useDetectVfxShots() {
  return useMutation({
    mutationFn: ({
      projectId,
      commitId,
      options,
    }: {
      projectId: string
      commitId: string
      options?: {
        vfx_marker_color?: string
        vfx_tag_name?: string
      }
    }) => vfxShotsApi.detectVfxShotsFromCommit(projectId, commitId, options),
    onSuccess: (data) => {
      toast.success(`Detected ${data.total_detected} VFX shot(s)`)
    },
    onError: (error: Error) => {
      toast.error(`Failed to detect VFX shots: ${error.message}`)
    },
  })
}

/**
 * Mark notification as read
 */
export function useMarkNotificationAsRead() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (notificationId: string) =>
      vfxShotsApi.markNotificationAsRead(notificationId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: vfxShotKeys.notifications(data.user_id),
      })
    },
    onError: (error: Error) => {
      toast.error(`Failed to mark notification as read: ${error.message}`)
    },
  })
}

/**
 * Bulk update VFX shot tags (add/remove tags for multiple shots)
 */
export function useBulkUpdateVfxShotTags() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      projectId,
      shotIds,
      addTags,
      removeTags
    }: {
      projectId: string
      shotIds: string[]
      addTags?: string[]
      removeTags?: string[]
    }) => {
      // Fetch current shots to compute new tags
      const shots = await Promise.all(
        shotIds.map(id => vfxShotsApi.getVfxShot(id))
      )

      // Update each shot's tags
      const updates = shots.map(async (shot) => {
        let newTags = shot.tags || []

        // Remove tags first
        if (removeTags && removeTags.length > 0) {
          newTags = newTags.filter(t => !removeTags.includes(t))
        }

        // Add tags
        if (addTags && addTags.length > 0) {
          // Add only tags that aren't already present
          const tagsToAdd = addTags.filter(t => !newTags.includes(t))
          newTags = [...newTags, ...tagsToAdd]
        }

        return vfxShotsApi.updateVfxShot(shot.id, { tags: newTags })
      })

      await Promise.all(updates)

      return { projectId, shotIds }
    },
    onSuccess: (data) => {
      // Invalidate all list queries for this project
      queryClient.invalidateQueries({
        predicate: (query) => {
          const queryKey = query.queryKey
          return (
            Array.isArray(queryKey) &&
            queryKey[0] === 'vfx-shots' &&
            queryKey[1] === 'list' &&
            queryKey[2] === data.projectId
          )
        },
      })
      toast.success('Tags updated successfully')
    },
    onError: (error: Error) => {
      toast.error(`Failed to update tags: ${error.message}`)
    }
  })
}
